'use client';

import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import CustomToast from './CustomToast';
import { setGlobalToastFunction, GlobalToastContext } from './toast';

// Define toast types
type ToastType = 'success' | 'error' | 'info';

// Define toast data structure
interface Toast {
  id: string;
  message: string;
  type: ToastType;
  subMessage?: string;
}

// Define context type
interface ToastContextType {
  showToast: (message: string, type?: ToastType, subMessage?: string) => void;
  hideToast: (id: string) => void;
}

// Create context with default values
const ToastContext = createContext<ToastContextType>({
  showToast: () => {},
  hideToast: () => {},
});

// Custom hook to use toast
export const useToast = () => useContext(ToastContext);

// Toast provider component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Function to add a toast
  const showToast = useCallback((message: string, type: ToastType = 'success', subMessage?: string) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type, subMessage }]);
    
    // Auto-dismiss after 2 seconds
    setTimeout(() => {
      hideToast(id);
    }, 2000);
  }, []);

  // Function to remove a toast
  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Set the global toast function so it can be used outside of React components
  useEffect(() => {
    setGlobalToastFunction(showToast);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      <GlobalToastContext.Provider value={{ showToast }}>
        {children}
        <div className="toast-container">
          {toasts.map((toast) => (
            <CustomToast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              subMessage={toast.subMessage}
              onClose={() => hideToast(toast.id)}
            />
          ))}
        </div>
      </GlobalToastContext.Provider>
    </ToastContext.Provider>
  );
};

export default ToastProvider;